import React from "react";
import { DevicePairingEvent } from "../../schemas/deviceSchema";
import { formatDisplayDateTime } from "../../utils/dateUtils";

/**
 * Shared renderer for a pairing-history timeline.
 *
 * - variant="device": one physical TV's journey — shows the SCREEN column
 *   (where it was at each event). Reached from a device.
 * - variant="screen": one location's device history — shows the DEVICE (code)
 *   column (which TV served it). Reached from a screen.
 */
interface PairingHistoryTableProps {
  events: DevicePairingEvent[];
  variant: "device" | "screen";
}

const EVENT_STYLES: Record<string, { label: string; className: string }> = {
  REGISTERED: { label: "Registered", className: "bg-blue-100 text-blue-800" },
  CODE_ISSUED: {
    label: "Code Issued",
    className: "bg-purple-100 text-purple-800",
  },
  ASSIGNED: { label: "Assigned", className: "bg-green-100 text-green-800" },
  UNASSIGNED: { label: "Unassigned", className: "bg-amber-100 text-amber-800" },
  DEACTIVATED: { label: "Deactivated", className: "bg-red-100 text-red-800" },
  RESUMED: { label: "Resumed", className: "bg-teal-100 text-teal-800" },
};

function renderEventBadge(event: string) {
  const style = EVENT_STYLES[event] ?? {
    label: event,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  );
}

function renderDetails(event: DevicePairingEvent) {
  const parts: string[] = [];
  const metadata = event.metadata ?? {};

  if (typeof metadata.previousCode === "string" && metadata.previousCode) {
    parts.push(`replaced ${metadata.previousCode}`);
  }
  if (typeof metadata.note === "string" && metadata.note) {
    parts.push(`"${metadata.note}"`);
  }
  if (typeof metadata.reason === "string" && metadata.reason) {
    parts.push(metadata.reason.toLowerCase().replace(/_/g, " "));
  }

  return parts.length ? parts.join(" · ") : "—";
}

function renderActor(event: DevicePairingEvent) {
  return (
    event.actorName ||
    (event.source === "DEVICE" ? "Device" : event.source)
  );
}

export default function PairingHistoryTable({
  events,
  variant,
}: PairingHistoryTableProps) {
  const isDeviceVariant = variant === "device";

  return (
    <div className="overflow-auto max-h-[65vh] border rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
              When
            </th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
              Event
            </th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
              {isDeviceVariant ? "Code" : "Device Code"}
            </th>
            {isDeviceVariant && (
              <th className="px-4 py-2 text-left font-medium text-gray-600">
                Screen
              </th>
            )}
            <th className="px-4 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
              By
            </th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 w-full">
              Details
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {events.map((event) => (
            <tr key={event.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                {formatDisplayDateTime(event.createdAt)}
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                {renderEventBadge(event.event)}
              </td>
              <td className="px-4 py-2 font-mono whitespace-nowrap text-gray-700">
                {event.verificationCode || "—"}
              </td>
              {isDeviceVariant && (
                <td className="px-4 py-2 text-gray-700">
                  {event.screenName || "—"}
                </td>
              )}
              <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                {renderActor(event)}
              </td>
              <td className="px-4 py-2 text-gray-500">{renderDetails(event)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
